/**
 * Score - point and progress tracking for a single run.
 *
 * Time bonus is computed at finalize() from elapsed run time:
 *   - <= GRACE seconds: full TIME_BONUS_MAX
 *   - GRACE..GRACE+DURATION: linearly decays to 0
 *   - > GRACE+DURATION: 0
 *
 * Only complete runs (all coconuts collected) earn a time bonus.
 */

export const POINTS_VISIBLE = 100;
export const POINTS_INVISIBLE = 250;

export const TIME_BONUS_MAX = 1500;
/** Free max-bonus window at the start (seconds). */
export const TIME_BONUS_GRACE_SEC = 30;
/** Length of the decay window (seconds). */
export const TIME_BONUS_DECAY_SEC = 150;

export interface ScoreSummary {
  /** Final score: basePoints + timeBonus. */
  total: number;
  basePoints: number;
  timeBonus: number;
  coconutsCollected: number;
  invisibleCollected: number;
  totalCoconuts: number;
  elapsedMs: number;
  /** True if all coconuts were collected. */
  complete: boolean;
}

export class Score {
  basePoints = 0;
  coconutsCollected = 0;
  invisibleCollected = 0;

  collect(invisible: boolean): number {
    const pts = invisible ? POINTS_INVISIBLE : POINTS_VISIBLE;
    this.basePoints += pts;
    this.coconutsCollected++;
    if (invisible) this.invisibleCollected++;
    return pts;
  }

  static computeTimeBonus(elapsedMs: number): number {
    const sec = elapsedMs / 1000;
    if (sec <= TIME_BONUS_GRACE_SEC) return TIME_BONUS_MAX;
    const overflow = sec - TIME_BONUS_GRACE_SEC;
    if (overflow >= TIME_BONUS_DECAY_SEC) return 0;
    return Math.round(TIME_BONUS_MAX * (1 - overflow / TIME_BONUS_DECAY_SEC));
  }

  buildSummary(
    elapsedMs: number,
    totalCoconuts: number,
    complete: boolean
  ): ScoreSummary {
    const timeBonus = complete ? Score.computeTimeBonus(elapsedMs) : 0;
    return {
      total: this.basePoints + timeBonus,
      basePoints: this.basePoints,
      timeBonus,
      coconutsCollected: this.coconutsCollected,
      invisibleCollected: this.invisibleCollected,
      totalCoconuts,
      elapsedMs,
      complete,
    };
  }
}

export function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
